/**
 * Bridge-on-delete: rewire dependency conditions around deleted node(s).
 *
 * When a node D is deleted and the user chooses to "bridge", every surviving
 * task that referenced D in its unlock conditions gets D's own upstream
 * condition expression substituted in place of the reference to D — preserving
 * AND/OR semantics exactly (per the left-to-right block evaluation used in
 * evaluateConditions.js).
 *
 * Example: C unlocks on (B AND X), B unlocks on (P OR Q).
 * Deleting B with bridge → C unlocks on ((P OR Q) AND X).
 *
 * Strategy:
 * - Simple cases (deleted node resolves to a single upstream or none) are
 *   patched in place, preserving the surviving task's block structure.
 * - Complex cases are rebuilt via boolean AST → DNF (OR of AND-blocks), which
 *   the v2 block format can represent exactly.
 */

import {
  getEffectiveConditionsV2,
  getAllConditionTaskIds,
} from './ConditionsDropdown/utils/conditionHelpers';
import { getWaitSeconds } from './ConditionsDropdown/utils/conditionWait';
import { getNestDependencyInfo } from './nestDependencyInfo';

// Guard against pathological DNF blowup (never expected on real graphs).
const MAX_DNF_TERMS = 64;

// ─── Boolean AST ────────────────────────────────────────────────
// null = "no condition" (always satisfied / reference removed)
// { type: 'leaf', taskId, branch }
// { type: 'op', op: 'AND'|'OR', a, b }

const makeLeaf = (taskId, branch) => ({ type: 'leaf', taskId, branch: branch || null });

/** Combine two ASTs with an operator; tolerates null operands. */
function combine(op, a, b) {
  if (a == null) return b;
  if (b == null) return a;
  return { type: 'op', op, a, b };
}

/** Build AST from one v2 block (conditions joined by innerOperator). */
function blockToAst(block) {
  const op = block.innerOperator || 'AND';
  let ast = null;
  for (const c of block.conditions || []) {
    if (!c?.taskId) continue;
    ast = combine(op, ast, makeLeaf(c.taskId, c.branch));
  }
  return ast;
}

/**
 * Build AST from v2 conditions, folding blocks left-to-right with their
 * outerOperator (matches evaluateUnlockConditions semantics).
 */
function conditionsToAst(v2) {
  let ast = null;
  const blocks = v2?.blocks || [];
  for (let i = 0; i < blocks.length; i++) {
    const blockAst = blockToAst(blocks[i]);
    if (!blockAst) continue;
    const op = blocks[i].outerOperator || 'AND';
    ast = combine(op, ast, blockAst);
  }
  return ast;
}

/**
 * Replace leaves in an AST.
 * resolveLeaf(leaf) returns: undefined = keep leaf, null = remove leaf,
 * or a replacement AST. Removed leaves collapse their parent operator.
 */
function substituteAst(ast, resolveLeaf) {
  if (!ast) return null;
  if (ast.type === 'leaf') {
    const replacement = resolveLeaf(ast);
    return replacement === undefined ? ast : replacement;
  }
  const a = substituteAst(ast.a, resolveLeaf);
  const b = substituteAst(ast.b, resolveLeaf);
  if (a === ast.a && b === ast.b) return ast;
  return combine(ast.op, a, b);
}

// ─── DNF conversion ─────────────────────────────────────────────

const leafKey = (leaf) => `${leaf.taskId}::${leaf.branch || ''}`;

/** term = Map(leafKey → leaf). isSubset(a, b): every leaf of a is in b. */
function isSubset(a, b) {
  if (a.size > b.size) return false;
  for (const k of a.keys()) {
    if (!b.has(k)) return false;
  }
  return true;
}

/** Remove duplicate terms and terms absorbed by a subset term. */
function absorbTerms(terms) {
  const result = [];
  for (const t of terms) {
    if (result.some((r) => isSubset(r, t))) continue; // t is redundant
    for (let i = result.length - 1; i >= 0; i--) {
      if (isSubset(t, result[i])) result.splice(i, 1);
    }
    result.push(t);
  }
  return result;
}

/** AST → array of AND-terms (OR of terms). null AST → [] (no conditions). */
function astToDnf(ast) {
  if (!ast) return [];
  if (ast.type === 'leaf') return [new Map([[leafKey(ast), ast]])];

  const A = astToDnf(ast.a);
  const B = astToDnf(ast.b);

  if (ast.op === 'OR') {
    return absorbTerms([...A, ...B]).slice(0, MAX_DNF_TERMS);
  }

  // AND: cross product
  if (A.length === 0) return B;
  if (B.length === 0) return A;
  const out = [];
  for (const t1 of A) {
    for (const t2 of B) {
      const merged = new Map(t1);
      for (const [k, v] of t2) merged.set(k, v);
      out.push(merged);
      if (out.length >= MAX_DNF_TERMS * 4) break;
    }
    if (out.length >= MAX_DNF_TERMS * 4) break;
  }
  return absorbTerms(out).slice(0, MAX_DNF_TERMS);
}

/** DNF terms → v2 conditions (blocks of ANDs joined by OR). */
function dnfToV2(terms) {
  return {
    version: 2,
    blocks: terms.map((term, i) => ({
      conditions: [...term.values()].map((l) => ({ taskId: l.taskId, branch: l.branch || null })),
      innerOperator: 'AND',
      outerOperator: i === 0 ? null : 'OR',
    })),
  };
}

// ─── Bridging ───────────────────────────────────────────────────

/**
 * Resolve each deleted task's upstream expression to an AST containing only
 * surviving task leaves (references between deleted tasks are chased
 * transitively; cycles resolve to null).
 */
function resolveDeletedAsts(tasks, deletedSet) {
  const byId = new Map(tasks.map((t) => [t.id, t]));
  const rawAst = new Map();
  for (const id of deletedSet) {
    const task = byId.get(id);
    rawAst.set(id, task ? conditionsToAst(getEffectiveConditionsV2(task)) : null);
  }

  const resolved = new Map();
  const resolving = new Set();
  const resolve = (id) => {
    if (resolved.has(id)) return resolved.get(id);
    if (resolving.has(id)) return null;
    resolving.add(id);
    const ast = substituteAst(rawAst.get(id) || null, (leaf) =>
      deletedSet.has(leaf.taskId) ? resolve(leaf.taskId) : undefined
    );
    resolving.delete(id);
    resolved.set(id, ast);
    return ast;
  };
  for (const id of deletedSet) resolve(id);
  return resolved;
}

/**
 * In-place patch used when every deleted reference resolves to a single
 * surviving leaf (or nothing): preserves the task's existing block structure.
 */
function patchBlocksInPlace(v2, resolved, deletedSet, selfId) {
  const blocks = [];
  for (const block of v2.blocks || []) {
    const conditions = [];
    const seen = new Set();
    for (const c of block.conditions || []) {
      if (!c?.taskId) continue;
      const waitSeconds = getWaitSeconds(c);
      let entry = {
        taskId: c.taskId,
        branch: c.branch || null,
        ...(waitSeconds ? { waitSeconds } : {}),
      };
      if (deletedSet.has(c.taskId)) {
        const r = resolved.get(c.taskId);
        if (!r) continue; // deleted node had no upstream → drop reference
        entry = { taskId: r.taskId, branch: r.branch || null };
      }
      if (entry.taskId === selfId) continue;
      const key = leafKey(entry);
      if (seen.has(key)) continue;
      seen.add(key);
      conditions.push(entry);
    }
    if (conditions.length > 0) blocks.push({ ...block, conditions });
  }
  if (blocks.length > 0) blocks[0] = { ...blocks[0], outerOperator: null };
  return { version: 2, blocks };
}

/**
 * Compute new unlockConditions for surviving tasks so that connections are
 * bridged through the deleted node(s).
 *
 * @param {Array} tasks - all tasks (pre-deletion)
 * @param {Array<string>} deletedIds - ids being deleted
 * @returns {Object} map of survivingTaskId → new v2 unlockConditions
 *   (only contains tasks that referenced a deleted id)
 */
export function computeBridgedConditionsForDeletion(tasks, deletedIds) {
  const deletedSet = new Set(deletedIds);
  const resolved = resolveDeletedAsts(tasks, deletedSet);

  const patch = {};
  for (const task of tasks) {
    if (deletedSet.has(task.id)) continue;

    const v2 = getEffectiveConditionsV2(task);
    const refIds = getAllConditionTaskIds(v2);
    const deletedRefs = refIds.filter((id) => deletedSet.has(id));
    if (deletedRefs.length === 0) continue;

    const allSimple = deletedRefs.every((id) => {
      const r = resolved.get(id);
      return r == null || r.type === 'leaf';
    });

    if (allSimple) {
      patch[task.id] = patchBlocksInPlace(v2, resolved, deletedSet, task.id);
    } else {
      let ast = substituteAst(conditionsToAst(v2), (leaf) =>
        deletedSet.has(leaf.taskId) ? resolved.get(leaf.taskId) : undefined
      );
      // Prune self-references that may have arrived via substitution
      ast = substituteAst(ast, (leaf) => (leaf.taskId === task.id ? null : undefined));
      patch[task.id] = dnfToV2(astToDnf(ast));
    }
  }
  return patch;
}

/**
 * Decide whether deleting these tasks warrants a bridge prompt: true only
 * when bridging would produce a different result than plainly breaking the
 * connections (i.e. some surviving task references a deleted node that has
 * upstream connections of its own).
 *
 * @returns {{ canBridge: boolean, upstreamCount: number, downstreamCount: number }}
 */
export function getBridgeDeletionInfo(tasks, deletedIds) {
  const deletedSet = new Set(deletedIds);
  const info = getNestDependencyInfo(tasks, deletedSet);

  let canBridge = false;
  if (info.upstreamCount > 0 && info.downstreamCount > 0) {
    const resolved = resolveDeletedAsts(tasks, deletedSet);
    canBridge = tasks.some((task) => {
      if (deletedSet.has(task.id)) return false;
      const refIds = getAllConditionTaskIds(getEffectiveConditionsV2(task));
      return refIds.some((id) => deletedSet.has(id) && resolved.get(id));
    });
  }

  return {
    canBridge,
    upstreamCount: info.upstreamCount,
    downstreamCount: info.downstreamCount,
  };
}
